import currency from "currency.js";

/**
 * Sizes are formatted to 6 decimals places by default
 * @param size
 * @param decimals number of decimals for display
 * @param rawDecimals number of decimals in the raw format (default 18 for wei-like values)
 */
export const formatSizePrecision = (
  size: number | string | null | undefined,
  decimals: number = 6,
  rawDecimals: number = 18
) => {
  const sizeNum = Number(size);
  if (!Number.isFinite(sizeNum)) {
    return "-";
  }

  const absSize = Math.abs(sizeNum);

  // If the number is extremely large (> 1e12), it's likely in raw format and needs conversion
  // API returns amounts as large integers with `rawDecimals` decimals (wei-like format)
  if (sizeNum > 1e12) {
    return formatSizePrecision(sizeNum / 10 ** rawDecimals, decimals, rawDecimals);
  }

  if (absSize > 0 && absSize < 0.0001) {
    return formatWithSubscript(sizeNum, 0.0001, 4);
  }

  const fixedSize = sizeNum.toFixed(decimals);
  if (Number(fixedSize) === 0 && absSize > 0) {
    return formatDecimalWithSubscript(sizeNum, {
      precision: decimals,
      maxPrecision: Math.max(decimals, 8),
    });
  }

  return fixedSize;
};

/**
 * Subscript digits for compact number notation
 */
const SUBSCRIPT_DIGITS: { [key: string]: string } = {
  '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄',
  '5': '₅', '6': '₆', '7': '₇', '8': '₈', '9': '₉',
};

/**
 * Format small numbers with subscript notation
 * e.g., 0.00001206 becomes "0.0₄1206" (4 zeros after decimal)
 * e.g., 0.00000009065 becomes "0.0₇9065" (7 zeros after decimal)
 *
 * @param value - The number to format
 * @param threshold - Numbers smaller than this use subscript notation (default 0.0001)
 * @param significantDigits - Number of significant digits to show (default 4)
 * @returns Formatted string with subscript notation for small numbers
 */
export const formatWithSubscript = (
  value: number | string,
  threshold: number = 0.0001,
  significantDigits: number = 4
): string => {
  const num = Number(value);

  if (num === 0) return "0";
  if (Number.isNaN(num)) return "0";

  const absNum = Math.abs(num);

  // Only use subscript for small positive numbers below threshold
  if (absNum >= threshold || absNum === 0) {
    return currency(num, { precision: significantDigits }).format({ symbol: "" });
  }

  // Convert to string and find zeros after decimal
  const str = absNum.toExponential(significantDigits - 1);
  const [mantissa, exponent] = str.split('e');
  const exp = Math.abs(parseInt(exponent, 10));

  // Number of zeros after "0." is (exponent - 1)
  const zerosCount = exp - 1;

  if (zerosCount <= 0) {
    return currency(num, { precision: significantDigits }).format({ symbol: "" });
  }

  // Get significant digits from mantissa (remove decimal point)
  const sigDigits = mantissa.replace('.', '');

  // Convert zeros count to subscript
  const subscriptZeros = String(zerosCount)
    .split('')
    .map(d => SUBSCRIPT_DIGITS[d])
    .join('');

  // Format: "0.0₄1206" where ₄ means 4 zeros
  const sign = num < 0 ? '-' : '';
  return `${sign}0.0${subscriptZeros}${sigDigits}`;
};

type SubscriptNumberFormatOptions = {
  precision?: number;
  maxPrecision?: number;
  subscriptThreshold?: number;
  significantDigits?: number;
  trimTrailingZeros?: boolean;
};

const trimTrailingDecimalZeros = (value: string): string => {
  if (!value.includes(".")) {
    return value;
  }

  return value.replace(/(\.\d*?[1-9])0+$/, "$1").replace(/\.0+$/, "");
};

/**
 * Format a decimal while preserving tiny non-zero values.
 * Values that would round to zero are expanded, and very small values use
 * compact subscript notation, e.g. 0.0000062 -> 0.0₅6200.
 */
export const formatDecimalWithSubscript = (
  value: number | string | null | undefined,
  options: SubscriptNumberFormatOptions = {}
): string => {
  const {
    precision = 2,
    maxPrecision = Math.max(precision, 6),
    subscriptThreshold = 0.0001,
    significantDigits = 4,
    trimTrailingZeros = true,
  } = options;
  const num = Number(value);

  if (!Number.isFinite(num)) {
    return "-";
  }

  if (num === 0) {
    return currency(0, { precision }).format({ symbol: "" });
  }

  const absNum = Math.abs(num);
  if (absNum < subscriptThreshold) {
    return formatWithSubscript(num, subscriptThreshold, significantDigits);
  }

  const fixed = currency(num, { precision }).format({ symbol: "" });
  if (Number(num.toFixed(precision)) !== 0) {
    return fixed;
  }

  const adaptivePrecision = Math.min(
    maxPrecision,
    Math.max(precision, Math.ceil(-Math.log10(absNum)) + 2)
  );
  const expanded = currency(num, { precision: adaptivePrecision }).format({
    symbol: "",
  });

  return trimTrailingZeros ? trimTrailingDecimalZeros(expanded) : expanded;
};

type SubscriptCurrencyFormatOptions = SubscriptNumberFormatOptions & {
  symbol?: string;
  showPositiveSign?: boolean;
};

/**
 * Currency formatter for trading PnL/fees where sub-cent non-zero values
 * should be readable instead of rounded to $0.00.
 */
export const formatCurrencyWithSubscript = (
  value: number | string | null | undefined,
  options: SubscriptCurrencyFormatOptions = {}
): string => {
  const { symbol = "$", showPositiveSign = false, ...numberOptions } = options;
  const num = Number(value);

  if (!Number.isFinite(num)) {
    return "-";
  }

  const sign = num < 0 ? "-" : showPositiveSign && num > 0 ? "+" : "";
  const formatted = formatDecimalWithSubscript(Math.abs(num), numberOptions);

  return `${sign}${symbol}${formatted}`;
};

/**
 * Format size for orderbook display
 * Uses subscript notation for very small values
 */
export const formatOrderbookSize = (
  size: number | string,
  precision: number = 6
): string => {
  const num = Number(size);

  if (num === 0) return "";
  if (Number.isNaN(num)) return "";

  // Use subscript notation for small values
  if (Math.abs(num) < 0.0001) {
    return formatWithSubscript(num, 0.0001, 4);
  }

  return currency(num, { precision }).format({ symbol: "" });
};
